import { useState, useEffect } from 'react'
import { NextPage } from 'next'
import Head from 'next/head'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'next/router'
import { useAccount } from 'wagmi'
import { useWaitingTime } from '../hooks'
import Footer from '~/components/Footer'
import Header from '~/components/Header'
import {
  StyledStakingPage,
  StyledContent,
  StyledStakingBox,
} from '../../styled'
import FormHeader from '../components/FormHeader'
import Tabs from '../components/Tabs'
import BrandWatermark from '../components/BrandWatermark'
import FaqSection from '../components/FaqSection'
import WithdrawalSection from './WithdrawalSection'
import ClaimSection from './ClaimSection'
import { StyledStakeSection, StyledStakeDescription } from '../styled'
import {
  StyledModeDescription,
  StyledWithdrawalOptions,
  StyledWithdrawalOption,
  StyledOptionTitle,
  StyledOptionTime,
} from './styled'
import ImgLidoWithdrawalFlow from '~/assets/images/lido-withdrawal-flow.png'
import ImgTokenlon from '~/assets/images/protocol-logo-tokenlon-with-bg.svg'
import ImgLido from '~/assets/images/logo-lido-with-bg.png'
import { useModalToggle } from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import { useIsDisableTrade } from '~/state/dex/hooks'
import { useLocalStorage } from '~/hooks/useLocalStorage'
import { lidoServiceWarningId } from '~/components/Modals'
import { useGA } from '~/hooks'
import { getFromParam } from '../utils'

const faqItems = [
  {
    id: 'lido-withdrawal-faq-1',
    question: 'lido_withdrawal_faq.question_1',
    answer: 'lido_withdrawal_faq.answer_1',
  },
  {
    id: 'lido-withdrawal-faq-2',
    question: 'lido_withdrawal_faq.question_2',
    answer: 'lido_withdrawal_faq.answer_2',
  },
  {
    id: 'lido-withdrawal-faq-3',
    question: 'lido_withdrawal_faq.question_3',
    answer: 'lido_withdrawal_faq.answer_3',
  },
]

const withdrawalModes = [
  {
    id: 'lido',
    title: 'Lido',
    logo: ImgLido.src,
  },
  {
    id: 'tokenlon',
    title: 'Tokenlon',
    logo: ImgTokenlon,
  },
]

const LidoWithdrawal: NextPage = () => {
  const { t } = useTranslation()
  const { trackView, trackClick } = useGA()
  const router = useRouter()
  const { address: account } = useAccount()
  const isWalletConnected = !!account
  const [selectedMode, setSelectedMode] = useState<'lido' | 'tokenlon'>('lido')
  const [amount, setAmount] = useState('')
  const lidoWaitingTime = useWaitingTime(amount)
  const tokenlonWaitingTime = `~1-5 ${t('minutes')}`

  const isSelectedLido = selectedMode === 'lido'

  const isDisableTrade = useIsDisableTrade()
  const toggleIpBlockedModal = useModalToggle(ApplicationModal.IP_BLOCKED)

  const toggleThirdPartyServiceWarningModal = useModalToggle(
    ApplicationModal.THIRD_PARTY_SERVICE_WARNING,
  )
  const [warningVisited, setWarningVisited] = useLocalStorage(
    lidoServiceWarningId,
    false,
  )

  const handleSelectMode = (mode: 'lido' | 'tokenlon') => {
    const trackId =
      mode === 'lido' ? 'webtl_stk_withdraw_lido' : 'webtl_stk_withdraw_tl'
    trackClick(trackId)

    setSelectedMode(mode)
  }

  useEffect(() => {
    if (isDisableTrade) {
      toggleIpBlockedModal()
    }
  }, [isDisableTrade])

  useEffect(() => {
    trackView('webtl_stk_detail', {
      type: 'withdraw',
      from: getFromParam(router.query),
    })

    if (!warningVisited && !isDisableTrade) {
      toggleThirdPartyServiceWarningModal()
      setWarningVisited(true)
    }
  }, [])

  return (
    <StyledStakingPage>
      <Head>
        <title>{t('meta_staking_title')}</title>
        <meta name="description" content={t('meta_staking_description')} />
        <meta name="keywords" content={t('meta_staking_keywords')} />
      </Head>
      <Header />
      <StyledContent>
        <StyledStakingBox root>
          <FormHeader />
          <Tabs currentTab="withdrawal" />
          <StyledStakeSection>
            <StyledStakeDescription>
              <img
                src={ImgLidoWithdrawalFlow.src}
                width={80}
                height={60}
                alt="withdrawal flow"
              />
              <div className="description-text">
                {t('lido_withdrawal_description')}
              </div>
            </StyledStakeDescription>

            <div>
              <StyledModeDescription>
                {t('lido_withdrawal_mode_description')}
              </StyledModeDescription>

              <StyledWithdrawalOptions>
                {withdrawalModes.map((mode) => (
                  <StyledWithdrawalOption
                    key={mode.id}
                    selected={selectedMode === mode.id}
                    onClick={() =>
                      handleSelectMode(mode.id as 'lido' | 'tokenlon')
                    }
                  >
                    <StyledOptionTitle>
                      <img src={mode.logo} alt={mode.title} />
                      <span>{mode.title}</span>
                    </StyledOptionTitle>
                    <StyledOptionTime>
                      {mode.id === 'lido'
                        ? lidoWaitingTime.value
                        : tokenlonWaitingTime}
                    </StyledOptionTime>
                  </StyledWithdrawalOption>
                ))}
              </StyledWithdrawalOptions>
            </div>

            <WithdrawalSection
              isSelectedLido={isSelectedLido}
              amount={amount}
              setAmount={setAmount}
            />
            {isWalletConnected && isSelectedLido && <ClaimSection />}
            <BrandWatermark />
          </StyledStakeSection>
        </StyledStakingBox>
        <FaqSection faqItems={faqItems} from="withdraw" />
      </StyledContent>
      <Footer />
    </StyledStakingPage>
  )
}

export default LidoWithdrawal
